import { Injectable, inject } from '@angular/core';

import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import type { Hotspot, IntegrationKinds, IntegrationsElement } from '@core/integrations-types';
import { groupByKey } from '@core/utils-legacy';

import { IntegrationsApiService } from './integrations-api.service';
import { hotspotToSignature } from './utils';

export interface IntegrationElement {
  component: string;
  hotspot: Hotspot;
  inputs: {
    [key: string]: any;
  };
}

export interface IntegrationElementsResponse {
  elements: IntegrationElement[];
}

export interface ElementsWithSignature {
  signature: string;
  elements: IntegrationsElement[];
}

export interface FreshEmbedToken {
  token: string;
  partnerCodename: string;
}

export type IntegrationAbilityInfo = { kind: string; abilities: string[] };

@Injectable({ providedIn: 'root' })
export class IntegrationsHttpClient {
  private readonly integrationsApi = inject(IntegrationsApiService);

  public loadElements(hotspots: Hotspot[]): Observable<ElementsWithSignature[]> {
    return this.integrationsApi.post('elements', { hotspots }).pipe(
      catchError(() => of({ elements: [] })),
      map(({ elements }: IntegrationElementsResponse) => {
        const elementsByHotspot = groupByKey(
          elements.map(element => ({
            ...element,
            hotspot: hotspotToSignature(element.hotspot),
          })),
          'hotspot',
        );
        return hotspots.map(hotspot => {
          const signature = hotspotToSignature(hotspot);
          return {
            signature,
            elements: (elementsByHotspot[signature] || []).map(
              element =>
                <IntegrationsElement>{
                  ...element,
                  hotspot,
                },
            ),
          };
        });
      }),
    );
  }

  public refreshPartnerEmbedToken(partnerCodename: string): Observable<FreshEmbedToken> {
    return this.runWithContext(`p/${partnerCodename}`, () =>
      this.integrationsApi.post(`refresh-embed-token`, {}).pipe(
        map((payload: { embedToken: { token: string } }) => ({
          token: payload.embedToken.token,
          partnerCodename,
        })),
      ),
    );
  }

  public loadEnabledIntegrationKinds(): Observable<IntegrationKinds[]> {
    return this.integrationsApi.get('enabled-integration-kinds').pipe(map(data => data.kinds));
  }

  public loadIntegrationAbilities(kinds: string[]): Observable<IntegrationAbilityInfo[]> {
    return this.integrationsApi
      .post('integration-abilities', { kinds })
      .pipe(map(data => data.integrationAbilities));
  }

  public loadFeatureFlags(): Observable<Record<string, boolean>> {
    return this.integrationsApi
      .get<{ features: string[] }>('features')
      .pipe(map(({ features }) => Object.fromEntries(features.map(feature => [feature, true]))));
  }

  private runWithContext(context: string, run: Function): Observable<any> {
    const previousContext = this.integrationsApi.context;
    this.integrationsApi.context = context;

    return run().pipe(
      map((result: any) => {
        this.integrationsApi.context = previousContext;
        return result;
      }),
      catchError((error: any) => {
        this.integrationsApi.context = previousContext;
        return error;
      }),
    );
  }
}
